import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, AlertTriangle } from "lucide-react";

// Periodo di riferimento sinistri (anni) per prodotto — allineato al backend (sinistri.py)
export const SINISTRI_YEARS = {
  rc_economia_solidale: 5,
  infortuni_economia_solidale: 5,
  mrv_volontariato: 5,
  cat_nat_impresa: 5,
  multirischi_beni: 5,
};

const STATI = [
  { value: "liquidato", label: "Liquidato" },
  { value: "riservato", label: "Riservato / in corso" },
  { value: "senza_seguito", label: "Senza seguito" },
];

const emptyClaim = () => ({ anno: "", tipo: "", importo: "", stato: "liquidato", descrizione: "" });
export const sinistriValid = (v) => !!v && v.ha_sinistri !== null && v.ha_sinistri !== undefined && !!v.veridicita;

export function SinistriDeclaration({ value, onChange, periodoAnni = 5, testidPrefix = "sinistri" }) {
  const v = value || { ha_sinistri: null, periodo_anni: periodoAnni, veridicita: false, elenco: [] };
  const set = (patch) => onChange({ ...v, periodo_anni: periodoAnni, ...patch });
  const setClaim = (i, patch) => set({ elenco: (v.elenco || []).map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const pick = (has) => set(has
    ? { ha_sinistri: true, elenco: v.elenco?.length ? v.elenco : [emptyClaim()] }
    : { ha_sinistri: false, elenco: [] });

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium text-forest">Si sono verificati sinistri negli ultimi {periodoAnni} anni per rischi analoghi?</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {[["No, nessun sinistro", false], ["Sì, ci sono stati sinistri", true]].map(([label, val]) => (
            <button key={label} type="button" data-testid={`${testidPrefix}-${val ? "si" : "no"}`}
              onClick={() => pick(val)}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                v.ha_sinistri === val ? "border-forest bg-forest text-white" : "border-border text-forest/70 hover:bg-sage"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {v.ha_sinistri === true && (
        <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
          <div className="flex items-center gap-1.5 text-xs font-medium text-amber-800">
            <AlertTriangle className="h-3.5 w-3.5" /> La presenza di sinistri comporta l'invio all'Agenzia (referral tecnico).
          </div>
          {(v.elenco || []).map((c, i) => (
            <div key={i} data-testid={`${testidPrefix}-row-${i}`} className="space-y-2 rounded-lg border border-border/60 bg-white p-3">
              <div className="grid gap-2 sm:grid-cols-4">
                <Input data-testid={`${testidPrefix}-anno-${i}`} placeholder="Anno" value={c.anno} onChange={(e) => setClaim(i, { anno: e.target.value })} />
                <Input className="sm:col-span-2" data-testid={`${testidPrefix}-tipo-${i}`} placeholder="Tipo di sinistro" value={c.tipo} onChange={(e) => setClaim(i, { tipo: e.target.value })} />
                <Input data-testid={`${testidPrefix}-importo-${i}`} type="number" placeholder="Importo €" value={c.importo} onChange={(e) => setClaim(i, { importo: e.target.value })} />
              </div>
              <div className="grid gap-2 sm:grid-cols-4">
                <Select value={c.stato} onValueChange={(val) => setClaim(i, { stato: val })}>
                  <SelectTrigger data-testid={`${testidPrefix}-stato-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STATI.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
                <Input className="sm:col-span-3" data-testid={`${testidPrefix}-desc-${i}`} placeholder="Descrizione (facoltativa)" value={c.descrizione} onChange={(e) => setClaim(i, { descrizione: e.target.value })} />
              </div>
              <button type="button" data-testid={`${testidPrefix}-remove-${i}`} onClick={() => set({ elenco: v.elenco.filter((_, idx) => idx !== i) })}
                className="flex items-center gap-1 text-xs text-terracotta hover:underline"><Trash2 className="h-3 w-3" /> Rimuovi</button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" data-testid={`${testidPrefix}-add`}
            onClick={() => set({ elenco: [...(v.elenco || []), emptyClaim()] })} className="gap-1"><Plus className="h-3.5 w-3.5" /> Aggiungi sinistro</Button>
        </div>
      )}

      {v.ha_sinistri !== null && v.ha_sinistri !== undefined && (
        <label className="flex items-start gap-2 text-xs text-muted-foreground">
          <Checkbox data-testid={`${testidPrefix}-veridicita`} checked={!!v.veridicita} onCheckedChange={(val) => set({ veridicita: !!val })} />
          <span>Dichiaro che le informazioni sui sinistri sopra riportate sono veritiere e complete.</span>
        </label>
      )}
    </div>
  );
}
